import axios from 'axios';
import {IRoute} from 'app/shared/model/route.model';
import {cleanEntity} from "app/shared/util/entity-utils";

/**
 * Route Service
 * Contains synchronous API calls for route functionality
 */
class RouteService {
  private apiUrl = 'api/routes';

  /**
   * Create a new route
   */
  async createRoute(route: IRoute): Promise<IRoute> {
    const requestUrl = `${this.apiUrl}`;
    const response = await axios.post<IRoute>(requestUrl, cleanEntity(route));
    // check for error response
    if (!response.data.id) {
      // get error message from body or default to response status
      const error = 'Error creating route';
      return Promise.reject(error);
    }
    return response.data;
  }

  /**
   * Get routes from village to village
   */
  async getRoutesFromVillageToVillage(startVillageId: string, endVillageId: string): Promise<IRoute[]> {
    const requestUrl = `${this.apiUrl}/village-to-village?startVillageId=${startVillageId}&endVillageId=${endVillageId}`;
    const response = await axios.get<IRoute[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get routes from village to healthcare facility
   */
  async getRoutesFromVillageToHealthcareFacility(startVillageId: string, endFacilityId: string): Promise<IRoute[]> {
    const requestUrl = `${this.apiUrl}/village-to-healthcare-facility?startVillageId=${startVillageId}&endFacilityId=${endFacilityId}`;
    const response = await axios.get<IRoute[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get routes from healthcare facility to healthcare facility
   */
  async getRoutesFromHealthcareFacilityToHealthcareFacility(startFacilityId: string, endFacilityId: string): Promise<IRoute[]> {
    const requestUrl = `${this.apiUrl}/healthcare-facility-to-healthcare-facility?startFacilityId=${startFacilityId}&endFacilityId=${endFacilityId}`;
    const response = await axios.get<IRoute[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get driving distance (km) and duration (seconds) between two points via the backend.
   */
  async getLocationDirections(
    startFacilityId?: string,
    startVillageId?: string,
    endFacilityId?: string,
    endVillageId?: string
  ): Promise<{ distance: number; durationSeconds: number } | null> {
    const params: Record<string, string> = {};
    if (startFacilityId) params.startFacilityId = startFacilityId;
    if (startVillageId) params.startVillageId = startVillageId;
    if (endFacilityId) params.endFacilityId = endFacilityId;
    if (endVillageId) params.endVillageId = endVillageId;

    const response = await axios.get('api/location/directions', { params });
    return response.data ?? null;
  }

}

// Export singleton instance
export const routeService = new RouteService();
export default routeService;
